import { FC, ReactNode, createContext, useContext } from 'react'
import useId from '@ui-components-3/ui/lib/hooks/useId'
import { Dialog, DialogContent, DialogActions, DialogTitle } from 'ui-components-web'

const DirtyHeaderIdContext = createContext('')

type DialogContainerProps = {
  fullScreen?: boolean
  fullWidth?: boolean
  maxWidth?: 'lg' | 'md' | 'sm' | 'xl' | 'xs' | boolean
  onBackdropClick?: () => void
  onClose?: () => void
  onEnter?: () => void
  onEntered?: () => void
  onEntering?: () => void
  onExit?: () => void
  onExited?: () => void
  onExiting?: () => void
  open: boolean
  actions?: ReactNode
  title?: string
  header?: ReactNode
  titleClass?: string
  actionClass?: string
  contentClass?: string
  dialogClass?: string | { [key: string]: string | number }
  parentProps?: { [props: string]: any }
  titleTestId?: string
  [childProps: string]: any
  titleVariant?: 'h1' | 'h2' | 'h3'
}

const withDialog = (Child: FC<any>) => {
  function DialogContainer(props: DialogContainerProps) {
    const {
      fullScreen,
      fullWidth,
      maxWidth,
      onBackdropClick,
      onClose,
      onEnter,
      onEntered,
      onEntering,
      onExit,
      onExited,
      onExiting,
      open,
      actions,
      header = null,
      title,
      titleClass,
      actionClass,
      contentClass,
      dialogClass,
      parentProps,
      titleTestId,
      titleVariant = 'h1',
      ...childProps
    } = props

    const dialogProps = {
      ...parentProps,
      fullScreen,
      fullWidth,
      maxWidth,
      onBackdropClick,
      onClose,
      onEnter,
      onEntered,
      onEntering,
      onExit,
      onExited,
      onExiting,
      open,
    }

    const headerId = useId()
    const TitleComponent = titleVariant

    return (
      <DirtyHeaderIdContext.Provider value={headerId}>
        <Dialog {...dialogProps} aria-labelledby={headerId} className={dialogClass}>
          {header}
          {title && (
            <DialogTitle disableTypography className={titleClass} data-testid={titleTestId}>
              <TitleComponent id={headerId} className="typography-h4 text-neutral">
                {title}
              </TitleComponent>
            </DialogTitle>
          )}
          <DialogContent className={contentClass}>
            <Child open={open} onClose={onClose} {...childProps} />
          </DialogContent>
          {actions && <DialogActions className={actionClass}>{actions}</DialogActions>}
        </Dialog>
      </DirtyHeaderIdContext.Provider>
    )
  }

  return DialogContainer
}

export const useDialogHeaderIdDirty = () => useContext(DirtyHeaderIdContext)

export default withDialog
